import { UserSession } from '../config/UserSession';
import { CoreManifest } from '../manifest';
import { isProdVersion } from '../utils/env';

import { Logger } from './Logger';

declare global {
  interface Window {
    dataLayer: unknown[];
  }
}

const GTM_ID = 'GTM-MW237N8';
const GTM_ID_PROD = 'GTM-W98NJ2D';

/**
 * Retrieves the appropriate Google Tag Manager ID based on the application manifest.
 */
const gtmId = isProdVersion ? GTM_ID_PROD : GTM_ID;

interface LogData {
  event: string;
  gameType: string | null;
  userId: string | null;
  casinoId: string | null;
  tableId: string | null;
  selectedLang: string | null;
  dpRatio: number;
  opDimentions: string;
  loadingTime?: number;
}

class Gtag {
  private cache = new Array<LogData>();
  private isReady = false;
  private userId: string | null = null;
  private readonly tableId: string | null;
  private readonly casinoId: string | null;
  private readonly gameType: string | null;
  private readonly selectedLang: string | null;
  private readonly dpRatio = window.devicePixelRatio;
  private readonly opDimentions = window.innerWidth + 'x' + window.innerHeight;

  constructor() {
    window['dataLayer'] = window['dataLayer'] || [];
    window['dataLayer'].push({ 'gtm.start': Date.now(), event: 'gtm.js' });

    if (CoreManifest.useGtag) {
      const gtagJS = document.createElement('script');
      gtagJS.async = true;
      gtagJS.src = 'https://www.googletagmanager.com/gtm.js?id=' + gtmId;
      gtagJS.onload = (): void => {
        this.flushCache();
        this.isReady = true;
      };
      document.head.appendChild(gtagJS);
    }

    const userSession = UserSession.getSession();

    this.tableId = userSession?.tableId || null;
    this.casinoId = userSession?.casinoId || null;
    this.selectedLang = userSession?.lang || null;
    this.gameType = userSession?.gametype || null;
  }

  public updateUserId(userId: string): void {
    this.userId = userId;
  }

  public log(data: LogData): void {
    if (this.isReady) {
      this.sendData(data);
    } else {
      this.cache.push(data);
    }
  }

  private sendData(data: LogData): void {
    window.dataLayer.push(data);
  }

  private flushCache(): void {
    this.cache.forEach((data) => this.sendData(data));
    this.cache = new Array<LogData>();
  }

  private getBasePayload(): LogData {
    return {
      gameType: this.gameType,
      userId: this.userId,
      casinoId: this.casinoId,
      tableId: this.tableId,
      selectedLang: this.selectedLang,
      dpRatio: this.dpRatio,
      opDimentions: this.opDimentions,
    } as LogData;
  }

  public logEvent(event: string, duration: number | undefined): void {
    const data: LogData = { ...this.getBasePayload(), event };

    if (duration) {
      data.loadingTime = duration;
    }
    this.log(data);
  }

  public logUniqueEvent(event: string, payload: Record<string, unknown>): void {
    this.log({ ...payload, ...this.getBasePayload(), event });
  }
}

export const GtagLogger = new Gtag();

/**
 * Logs the start of game loading and marks performance in production environment.
 * Any exceptions during logging are caught and ignored.
 */
export const enableGtagLogger = (): void => {
  try {
    GtagLogger.logEvent('game-load-started', undefined);
    Logger.logEvent('gtagLoadStarted', { instant: true });
    performance.mark('loadStartForGtag');
  } catch (e) {
    //nothing we can do
  }
};
